import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'
import { formatCompact, formatMetricValue } from "../../lib/reportNew/formatNew"
import { metricFormat } from '../../lib/reportNew/aggregateNew'
import { AXIS_PROPS, GRID_PROPS, LEGEND_PROPS, TOOLTIP_PROPS, makeColorAt } from './chart-themeNew'

/**
 * Gráficos cartesianos genéricos (barras, líneas y áreas).
 *
 * Reciben datos ya agregados: `data` con { name, [serie]: valor } y la lista de
 * series a dibujar, por lo que el mismo componente sirve para cualquier
 * dimensión / métrica configurada por el usuario.
 */

/** Formatea cada serie con su propio formato cuando hay multi-métrica. */
const tooltipFormatter = (format, metricMap) => (value, name) => {
  const fmt = metricMap?.[name] ? metricFormat(metricMap[name]) : format
  return [formatMetricValue(value, fmt), name]
}

/** Recorta etiquetas muy largas para que el eje siga siendo legible. */
const shortLabel = (value) => {
  const text = String(value)
  return text.length > 16 ? `${text.slice(0, 15)}…` : text
}

function AxisSet({ format, horizontal, angledLabels }) {
  const valueAxis = { ...AXIS_PROPS, tickFormatter: (value) => formatCompact(value) }
  if (horizontal) {
    return (
      <>
        <XAxis type="number" {...valueAxis} />
        <YAxis
          type="category"
          dataKey="name"
          width={120}
          tickFormatter={shortLabel}
          {...AXIS_PROPS}
        />
      </>
    )
  }
  return (
    <>
      <XAxis
        dataKey="name"
        {...AXIS_PROPS}
        interval={0}
        tickFormatter={shortLabel}
        angle={angledLabels ? -35 : 0}
        textAnchor={angledLabels ? 'end' : 'middle'}
        height={angledLabels ? 78 : 28}
        tickMargin={angledLabels ? 4 : 8}
      />
      <YAxis width={54} {...valueAxis} />
    </>
  )
}

export function BarChartGraph({
  data,
  series,
  format,
  metricMap,
  stacked = false,
  horizontal = false,
  showLegend = true,
  colorByPoint = false,
  chartType = 'bar',
}) {
  const colorAt = makeColorAt(chartType)
  const angledLabels = !horizontal && data.length > 6
  return (
    <ResponsiveContainer width="100%" height="100%">
      <BarChart
        data={data}
        layout={horizontal ? 'vertical' : 'horizontal'}
        margin={{ top: 8, right: 12, left: 0, bottom: 0 }}
        barCategoryGap={horizontal ? '18%' : '26%'}
      >
        <CartesianGrid {...GRID_PROPS} vertical={horizontal} horizontal={!horizontal} />
        <AxisSet format={format} horizontal={horizontal} angledLabels={angledLabels} />
        <Tooltip {...TOOLTIP_PROPS} formatter={tooltipFormatter(format, metricMap)} />
        {showLegend && series.length > 1 && <Legend {...LEGEND_PROPS} />}
        {series.map((name, index) => (
          <Bar
            key={name}
            dataKey={name}
            name={name}
            stackId={stacked ? 'total' : undefined}
            fill={colorAt(index)}
            radius={
              horizontal
                ? [0, 4, 4, 0]
                : stacked && index < series.length - 1
                  ? [0, 0, 0, 0]
                  : [4, 4, 0, 0]
            }
            maxBarSize={horizontal ? 22 : 44}
          >
            {colorByPoint &&
              series.length === 1 &&
              data.map((entry, cellIndex) => (
                <Cell key={entry.__key || cellIndex} fill={colorAt(cellIndex)} />
              ))}
          </Bar>
        ))}
      </BarChart>
    </ResponsiveContainer>
  )
}

export function LineChartGraph({ data, series, format, metricMap, showLegend = true, chartType = 'line' }) {
  const colorAt = makeColorAt(chartType)
  return (
    <ResponsiveContainer width="100%" height="100%">
      <LineChart data={data} margin={{ top: 8, right: 12, left: 0, bottom: 0 }}>
        <CartesianGrid {...GRID_PROPS} />
        <AxisSet format={format} angledLabels={data.length > 8} />
        <Tooltip {...TOOLTIP_PROPS} cursor={{ stroke: '#c9d8da' }} formatter={tooltipFormatter(format, metricMap)} />
        {showLegend && series.length > 1 && <Legend {...LEGEND_PROPS} />}
        {series.map((name, index) => (
          <Line
            key={name}
            type="monotone"
            dataKey={name}
            name={name}
            stroke={colorAt(index)}
            strokeWidth={2}
            dot={{ r: 2.5, strokeWidth: 0, fill: colorAt(index) }}
            activeDot={{ r: 5 }}
          />
        ))}
      </LineChart>
    </ResponsiveContainer>
  )
}

export function AreaChartGraph({ data, series, format, metricMap, stacked = true, showLegend = true, chartType = 'area' }) {
  const colorAt = makeColorAt(chartType)
  return (
    <ResponsiveContainer width="100%" height="100%">
      <AreaChart data={data} margin={{ top: 8, right: 12, left: 0, bottom: 0 }}>
        <defs>
          {series.map((name, index) => (
            <linearGradient key={name} id={`area-${index}`} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={colorAt(index)} stopOpacity={0.55} />
              <stop offset="100%" stopColor={colorAt(index)} stopOpacity={0.06} />
            </linearGradient>
          ))}
        </defs>
        <CartesianGrid {...GRID_PROPS} />
        <AxisSet format={format} angledLabels={data.length > 8} />
        <Tooltip {...TOOLTIP_PROPS} cursor={{ stroke: '#c9d8da' }} formatter={tooltipFormatter(format, metricMap)} />
        {showLegend && series.length > 1 && <Legend {...LEGEND_PROPS} />}
        {series.map((name, index) => (
          <Area
            key={name}
            type="monotone"
            dataKey={name}
            name={name}
            stackId={stacked ? 'total' : undefined}
            stroke={colorAt(index)}
            strokeWidth={2}
            fill={`url(#area-${index})`}
          />
        ))}
      </AreaChart>
    </ResponsiveContainer>
  )
}
